import type { ReactNode } from 'react';
import type { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import type { Currency } from 'utils/Currency';
import type { addToCartArgsType } from 'components/shared/Cart/types';

export type SelectionsMap = Record<string, string | null | string[]>;

export type ResolveCtx = {
  store: ShopifyStore;
  currency: Currency;
  selections: SelectionsMap;
};

// A value that may be a literal or a function of runtime context. Lets configs
// declare per-store / per-selection values without switch statements in atoms.
export type Resolvable<T> = T | ((ctx: ResolveCtx) => T);

type OptionBase = {
  id: string;
  disabled?: Resolvable<boolean>;
  variantId?: Resolvable<string>;
  toCartItem?: (ctx: ResolveCtx) => addToCartArgsType[];
};

export type SwatchOption = OptionBase & {
  image: string;
  label: string;
  badge?: string;
};

export type LargePromptOption = OptionBase & {
  label?: string;
  title: ReactNode;
  price?: Resolvable<number>;
  strikedPrice?: Resolvable<number>;
  saveTag?: Resolvable<string>;
  secondaryText?: Resolvable<ReactNode>;
  bullets?: ReactNode[];
  isDecline?: boolean;
};

export type ImageCardOption = OptionBase & {
  image: string;
  title: ReactNode;
  price?: Resolvable<number>;
  tag?: string;
  subTitle?: ReactNode;
};

export type PillOption = OptionBase & {
  label: ReactNode;
  badge?: string;
};

// '*' matches any non-null selection; string / string[] match exact ids.
export type VisibleWhenSpec = Record<string, '*' | string | string[]>;

type CardBase = {
  id: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  visibleWhen?: VisibleWhenSpec;
  show?: (ctx: ResolveCtx) => boolean;
  info?: { content: ReactNode; ariaLabel?: string };
  banner?: { icon?: ReactNode; text: Resolvable<ReactNode> };
};

export type SwatchCardConfig = CardBase & {
  kind: 'swatch';
  options: SwatchOption[];
  selection?: 'single';
};

export type LargePromptCardConfig = CardBase & {
  kind: 'largePrompt';
  options: LargePromptOption[];
  selection?: 'single';
};

export type ImageCardConfig = CardBase & {
  kind: 'imageCard';
  options: ImageCardOption[];
  selection?: 'single';
};

export type PillGroupCardConfig = CardBase & {
  kind: 'pillGroup';
  options: PillOption[];
  selection?: 'single' | 'multi';
};

export type SubsectionCardConfig = CardBase & {
  kind: 'subsection';
  image?: string;
  child: CardConfig;
};

export type CompositeCardConfig = CardBase & {
  kind: 'composite';
  children: CardConfig[];
};

export type ActionCardConfig = CardBase & {
  kind: 'actionCard';
  open: (ctx: ResolveCtx) => void;
  activeRender?: (ctx: ResolveCtx) => ReactNode;
  idleLabel: ReactNode;
  declineOption?: PillOption;
};

export type CardConfig =
  | SwatchCardConfig
  | LargePromptCardConfig
  | ImageCardConfig
  | PillGroupCardConfig
  | SubsectionCardConfig
  | CompositeCardConfig
  | ActionCardConfig;

export type AnyOption =
  | SwatchOption
  | LargePromptOption
  | ImageCardOption
  | PillOption;

export const cardHasOptions = (
  card: CardConfig,
): card is
  | SwatchCardConfig
  | LargePromptCardConfig
  | ImageCardConfig
  | PillGroupCardConfig => {
  return (
    card.kind === 'swatch' ||
    card.kind === 'largePrompt' ||
    card.kind === 'imageCard' ||
    card.kind === 'pillGroup'
  );
};

export const resolve = <T>(value: Resolvable<T> | undefined, ctx: ResolveCtx): T | undefined => {
  if (typeof value === 'function') {
    return (value as (ctx: ResolveCtx) => T)(ctx);
  }
  return value;
};

export const matchesVisibleWhen = (
  spec: VisibleWhenSpec,
  selections: SelectionsMap,
): boolean => {
  for (const groupId of Object.keys(spec)) {
    const expected = spec[groupId];
    const actual = selections[groupId];
    if (expected === '*') {
      if (actual == null) return false;
      if (Array.isArray(actual) && actual.length === 0) return false;
    } else if (Array.isArray(expected)) {
      if (typeof actual !== 'string' || !expected.includes(actual)) return false;
    } else {
      if (actual !== expected) return false;
    }
  }
  return true;
};
